import type { GuideChapter } from '../types'
import { n } from '../types'

export const service: GuideChapter = {
  id: 'service',
  title: 'Service og udstyr',
  icon: '🔋',
  intro: `Enheder hos kunderne med QR-mærkat, servicebesøg med checkliste og underskrift - og husets eget udstyr: instrumenter, værktøj, nøgler, certifikater og fravær.`,
  sections: [
    {
      id: 'service-visit',
      title: 'Udfør service på en enhed',
      icon: '📷',
      role: 'Medarbejder',
      module: 'service',
      where: `"Service" i bundmenuen - scan mærkatet på enheden`,
      intro: `Scan QR-mærkatet, tryk "Start service", gå checklisten igennem, tag to fotos og skriv under. Er alt i orden, kan godkendelsen hentes som PDF.`,
      sketch: {
        device: 'phone',
        title: 'Service · Batteri, BESS-anlæg',
        nav: true,
        blocks: [
          { t: 'field', label: 'Målt med · Instrument *', value: 'Fluke 1664 FC', n: 1 },
          {
            t: 'card',
            title: 'Checkliste (4 af 9)',
            children: [
              { t: 'text', text: 'Visuelt eftersyn af kabinet' },
              { t: 'btns', items: [{ text: 'I orden', primary: true, n: 2 }, { text: 'Afvigelse' }, { text: 'Ikke relevant' }] },
              { t: 'field', label: 'Isolationsmodstand (MΩ) · Forventet ≥ 1', value: '250' }
            ]
          },
          { t: 'cols', left: [{ t: 'photo', label: 'Foto af enheden', n: 3 }], right: [{ t: 'photo', label: 'Servicemærkat' }] },
          { t: 'field', label: 'Udført af *', value: 'Jens Montør' },
          { t: 'sign', n: 4 },
          { t: 'btn', text: 'Afslut service', primary: true, n: 5 }
        ]
      },
      flows: [
        {
          title: 'Et almindeligt eftersyn',
          steps: [
            `Tryk "Service" i bundmenuen, tryk "Start kamera", og hold telefonen mod QR-koden. Enheden åbner.`,
            `Tryk "Start service".`,
            n(1, `Vælg instrumentet under "Målt med".`),
            n(2, `Gå punkterne igennem. Skriv målinger og tryk Enter efter hver.`),
            n(3, `Tag begge fotos.`),
            n(4, `Skriv dit navn, og skriv under med fingeren.`),
            n(5, `Tryk "Afslut service". Er der ingen afvigelser, tryk "Hent godkendelse (PDF)".`)
          ]
        },
        {
          title: 'QR-koden kan ikke læses',
          steps: [`Skriv de 8 tegn under QR-koden i "Eller tast koden", og tryk "Slå op".`]
        },
        {
          title: 'Du kan ikke komme til anlægget',
          steps: [`Tryk "Afbryd besøg" og bekræft. Et afbrudt besøg kan ikke tages op igen - start et nyt næste gang.`]
        }
      ],
      tips: [
        `Et afsluttet eller afbrudt besøg er låst og kan ikke rettes.`,
        `Godkendelsen kræver: ingen afvigelser, et valgt instrument ved målinger, og at instrumentets kalibrering var gyldig.`,
        `En måling uden for intervallet bliver automatisk en afvigelse.`,
        `Alt, du har udfyldt, gemmes undervejs. Gå tilbage til enheden og tryk "Fortsæt service".`
      ]
    },
    {
      id: 'replaced-parts',
      title: 'Skriv udskiftede dele på servicesagen',
      icon: '🔩',
      role: 'Medarbejder',
      module: 'service',
      where: `En servicesag → "Udskiftede dele" → "Tilføj udskiftet del"`,
      intro: `Har du skiftet noget - batterier, en sikring, en lampe - så skriv det på servicesagen og scan stregkoden på den nye del. Står varen i lageret, trækkes den fra din bil.`,
      sketch: {
        device: 'phone',
        title: 'Udskiftede dele',
        nav: true,
        blocks: [
          { t: 'field', label: 'Hvilken enhed sidder delen i? *', value: 'Batteribank A', n: 1 },
          { t: 'btn', text: 'Scan', primary: true, n: 2 },
          { t: 'field', label: 'Taget fra', value: 'Bil 3 (min bil)', n: 3 },
          { t: 'field', label: 'Antal (stk.) *', value: '4' },
          { t: 'field', label: 'Hvad er udskiftet? *', value: 'Skiftet 4 batterier, de gamle var svulmede', n: 4 },
          { t: 'field', label: 'Serienr. på den gamle del', value: 'BM-20231', n: 5 },
          { t: 'field', label: 'Skærmbillede af godkendelsen (frivilligt)', value: 'Vælg fil', n: 6 },
          { t: 'btn', text: 'Gem', primary: true, n: 7 }
        ]
      },
      flows: [
        {
          title: 'Skriv en udskiftet del',
          steps: [
            `Åbn servicesagen, og tryk "Tilføj udskiftet del" under "Udskiftede dele".`,
            n(1, `Vælg den enhed, delen sidder i. Har sagen kun én, er den valgt.`),
            n(2, `Tryk "Scan", og hold kameraet mod stregkoden på den nye del. Er den slidt, så tast tallene under stregkoden.`),
            n(3, `Står varen i lageret, vælger du, hvor den er taget fra - din bil er valgt på forhånd. Er den købt direkte, så vælg "Ikke fra lageret".`),
            n(4, `Skriv antal og hvad der er udskiftet - gerne hvorfor.`),
            n(5, `Skriv serienummeret på den gamle og den nye del, hvis de har et. De kan også skrives på bagefter.`),
            n(6, `Vælg skærmbilledet af godkendelsen, hvis udskiftningen er godkendt, og billedet efter udskiftningen. Begge er frivillige.`),
            n(7, `Tryk "Gem". Billederne lægges i enhedens mappe i SharePoint.`)
          ]
        },
        {
          title: 'En del, lageret ikke kender',
          steps: [
            `Scan stregkoden alligevel - den gemmes på sagen.`,
            `Skriv under "Hvad er delen?", hvad det er. Lageret røres ikke.`
          ]
        },
        {
          title: 'Fortryd en del',
          steps: [
            `Tryk "Fjern" ved delen. Kom den fra din bil, lægges den tilbage i bilen.`,
            `Du kan fjerne dine egne dele. En projektleder kan fjerne alle.`
          ]
        },
        {
          title: 'Godkendelse, billede efter og serienumre bagefter',
          steps: [
            `Tryk "📄 Godkendelse" ved delen, og vælg skærmbilledet af godkendelsen.`,
            `Tryk "📷 Efter udskiftning" for at tage et billede af den nye del, som den sidder.`,
            `Tryk "Serienumre" (eller "Ret serienumre") for at skrive den gamle og den nye dels serienummer.`,
            `Et billede, der er kommet forkert på, fjernes med ✕ under det.`
          ]
        }
      ],
      tips: [
        `Delene kommer også i kortet "Materialer" på sagen, når de er taget fra lageret.`,
        `Enhedens egen side viser alt, der er skiftet på den - også efter at sagen er lukket.`,
        `Serienumrene står på delen - enhedens eget serienummer ændres ikke.`,
        `Billederne ligger i enhedens mappe i SharePoint med datoen i navnet, f.eks. "2026-09-30 Godkendelse - Batterimodul".`
      ]
    },
    {
      id: 'devices',
      title: 'Enheder og deres historik',
      icon: '🔋',
      role: 'Medarbejder',
      module: 'service',
      where: `"Enheder" i menuen, eller scan mærkatet`,
      intro: `Enhedens side viser næste service, sidste eftersyn, afvigelser, målinger over tid, gamle fotos, garanti, manualer og enhedens egne dokumenter - f.eks. tavletegningen.`,
      flows: [
        {
          title: 'Slå historikken op foran anlægget',
          steps: [
            `Scan mærkatet.`,
            `Læs "Sidste eftersyn", "Afvigelser ved sidste eftersyn" og "Målinger over tid".`,
            `Tryk "Hent billederne" for at se fotos fra tidligere besøg.`
          ]
        },
        {
          title: 'Læg en tavletegning eller et datablad på enheden',
          steps: [
            `Åbn enheden, og find "Dokumenter".`,
            `Vælg hvad det er: Tavletegning, Datablad, Manual, Certifikat eller Andet. Skriv evt. en beskrivelse, f.eks. "Hovedtavle rev. B".`,
            `Tryk "Læg dokument op", og vælg filen. Dokumenterne står samlet pr. slags, så tegningen er let at finde.`,
            `Tryk på et dokument for at åbne det. Kun projektledere og op kan slette.`
          ]
        }
      ]
    },
    {
      id: 'devices-manage',
      title: 'Opret en enhed og print mærkat',
      icon: '🏷️',
      role: 'Projektleder',
      module: 'service',
      where: `"Enheder" → "Ny enhed"`,
      intro: `En enhed hører til kunden. Med et serviceinterval dukker den op på forsiden, når den skal have service.`,
      sketch: {
        device: 'desktop',
        title: 'Enhed · Batteri, BESS-anlæg',
        blocks: [
          {
            t: 'cols',
            left: [
              { t: 'field', label: 'Navn *', value: 'Batteri, BESS-anlæg' },
              { t: 'field', label: 'Kunde *', value: 'TC EL', n: 1 },
              { t: 'field', label: 'Service', value: 'Én gang om året', n: 2 }
            ],
            right: [
              { t: 'qr', label: 'K7M2QX9P', n: 3 },
              { t: 'btn', text: 'Print mærkat', primary: true, n: 4 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Opret en enhed',
          steps: [
            `Tryk "Ny enhed".`,
            n(1, `Udfyld navn og kunde. Vælg type og model i listerne - så foreslås intervallet, hvis typen har et krav, og fabrikatet udfyldes af modellen. Mangler typen eller modellen, så vælg "+ Ny type ..." eller "+ Ny model ..." nederst i listen.`),
            n(2, `Vælg interval under "Service".`),
            `Tryk "Opret enhed".`
          ]
        },
        {
          title: 'Print mærkatet',
          steps: [
            n(3, `Enheden har fået sin kode.`),
            n(4, `Tryk "Print mærkat", vælg antal kopier, og vælg "Printer": "Brother QL, 62 mm endeløs" eller "Ark med mærkater". Tryk "Print".`),
            `På Brother QL-1110NWB: vælg printeren, papirstørrelse 62 mm x 40 mm, margener "Ingen" og skalering 100 %. Hvert mærkat bliver klippet for sig.`
          ]
        },
        {
          title: 'Slet en enhed, der er oprettet ved en fejl (administrator)',
          steps: [
            `Åbn enheden, og tryk "Slet" øverst ved "Rediger". Bekræft.`,
            `Har enheden historik - servicebesøg, udskiftede dele, dokumenter eller et abonnement - kan den ikke slettes. Så står det på skærmen, og du retter den til udtjent i stedet.`
          ]
        }
      ],
      tips: [
        `Uden interval er enheden kun registreret og kommer aldrig på listen over forfalden service.`,
        `Typen afgør, hvilken servicecheckliste og hvilke manualer der foreslås - derfor vælges den fra en fast liste, så den staves ens overalt.`,
        `Findes papirstørrelsen 62 mm x 40 mm ikke i printvinduet, så opret den én gang i Brother-driverens indstillinger under "Papirstørrelse". Pc'en husker, om du printer på rulle eller ark.`
      ]
    },
    {
      id: 'checklists',
      title: 'Servicechecklister',
      icon: '🔧',
      role: 'Administrator',
      module: 'service',
      where: `Administration → "Servicechecklister"`,
      intro: `Checklisten, montøren går igennem. Den foreslås ud fra enhedens type.`,
      flows: [
        {
          title: 'Lav en checkliste',
          steps: [
            `Tryk "Ny checkliste".`,
            `Skriv navnet, og vælg enhedstypen i listen ("Alle typer", hvis den passer til alt).`,
            `Tilføj punkter: "I orden / afvigelse" eller "Måling" med enhed, mindst og højst.`,
            `Tryk "Gem".`
          ]
        }
      ],
      tips: [`Punkterne kopieres ind, når et besøg startes. Ændringer rører ikke gamle rapporter.`]
    },
    {
      id: 'device-types',
      title: 'Enhedstyper og modeller',
      icon: '🏷️',
      role: 'Administrator',
      module: 'service',
      where: `Administration → "Enhedstyper"`,
      intro: `Den faste liste, som type og model på enhederne vælges fra. Typen afgør, hvilken checkliste og hvilke manualer der foreslås.`,
      flows: [
        {
          title: 'Tilføj en type eller model',
          steps: [
            `Skriv typen i feltet øverst, og tryk "Tilføj type".`,
            `Under typen: skriv modellen og evt. fabrikatet, og tryk "Tilføj model". Fabrikatet udfyldes af sig selv på enheden, når modellen vælges.`,
            `Projektledere kan også tilføje direkte fra "Ny enhed" med "+ Ny type ..." og "+ Ny model ...".`
          ]
        },
        {
          title: 'Ret, slå fra eller slet',
          steps: [
            `"Omdøb" retter navnet på ALLE enheder, checklister og manualer med typen, så de bliver ved med at passe sammen.`,
            `"Slå fra" fjerner typen eller modellen fra listen. Enheder, der har den, beholder den.`,
            `"Slet" kan kun bruges, når ingen enhed, checkliste eller manual bruger typen.`
          ]
        }
      ],
      tips: [`Typen "Router" bruges af VPN: routeren på et nyt site oprettes som enhed med den type.`]
    },
    {
      id: 'instruments',
      title: 'Måleinstrumenter',
      icon: '📏',
      role: 'Medarbejder',
      module: 'instruments',
      where: `"Måleinstrumenter" i menuen`,
      intro: `Hvilke instrumenter findes, hvem har dem, og hvornår skal de kalibreres.`,
      flows: [{ title: 'Tjek dit instrument', steps: [`Søg på serienummeret, og læs kolonnen "Næste".`] }],
      tips: [`Et udløbet instrument kan vælges på et besøg - men så kan eftersynet ikke godkendes.`]
    },
    {
      id: 'instruments-manage',
      title: 'Opret instrumenter og skriv kalibreringer ind',
      icon: '🧪',
      role: 'Administrator',
      module: 'instruments',
      where: `"Måleinstrumenter"`,
      intro: `Administratorer opretter instrumenterne og registrerer kalibreringerne.`,
      flows: [
        { title: 'Nyt instrument', steps: [`Tryk "Nyt instrument", udfyld, og tryk "Gem".`] },
        { title: 'Kalibrering', steps: [`Tryk "Kalibrering" ud for instrumentet.`, `Udfyld dato og certifikatnummer, og tryk "Gem kalibrering".`] }
      ]
    },
    {
      id: 'manuals',
      title: 'Manualer',
      icon: '📖',
      role: 'Medarbejder',
      module: 'manuals',
      where: `"Manualer" i menuen - og på enhedens side`,
      intro: `Husets egne vejledninger og leverandørernes PDF'er. En manual, der hører til en enhedstype, står også på enhedens side.`,
      flows: [{ title: 'Find en manual', steps: [`Søg, og tryk på titlen.`, `Tryk "Hent dokumentet" for at se en PDF.`] }]
    },
    {
      id: 'manuals-write',
      title: 'Skriv eller læg en manual op',
      icon: '✍️',
      role: 'Projektleder',
      module: 'manuals',
      where: `"Manualer" → "Ny manual"`,
      intro: `Skriv manualen direkte i systemet, eller læg en færdig fil op.`,
      flows: [
        {
          title: 'Skriv en manual',
          steps: [
            `Tryk "Ny manual", skriv titel og vælg hylde.`,
            `Vælg "Skriv den her", og tilføj enhedstype.`,
            `Skriv teksten - brug knapperne til overskrift, punkter og billeder - og tryk "Gem".`
          ]
        },
        { title: 'Læg en PDF op', steps: [`Vælg "Læg en færdig fil op", tryk "Vælg dokument", og tryk "Gem".`] }
      ],
      tips: [`"Slags" kan ikke ændres bagefter. Hylderne styres af en administrator under "Manualhylder".`]
    },
    {
      id: 'warranties',
      title: 'Garantier og reklamationer',
      icon: '🛡️',
      role: 'Medarbejder',
      module: 'warranties',
      where: `"Garantier" i menuen`,
      intro: `Se om en garanti stadig løber, og hvem der hæfter - og meld en fejl, når kunden ringer.`,
      flows: [
        {
          title: 'Kunden ringer med en fejl',
          steps: [
            `Søg på kunden, og se om garantien løber, og hvem der hæfter.`,
            `Tryk "Meld fejl", beskriv den, og sæt datoen for, hvornår kunden meldte den.`,
            `Tryk "Meld fejlen".`
          ]
        }
      ],
      tips: [`Datoen, kunden meldte fejlen, afgør om kravet gælder - også hvis garantien er udløbet siden.`]
    },
    {
      id: 'warranties-manage',
      title: 'Opret garantier og afgør reklamationer',
      icon: '⚖️',
      role: 'Projektleder',
      module: 'warranties',
      where: `"Garantier"`,
      intro: `Projektledere opretter garantier og afgør reklamationerne.`,
      flows: [
        { title: 'Opret', steps: [`Tryk "Opret garanti", udfyld hvad den dækker, hvem der hæfter og hvor længe, og tryk "Gem".`] },
        { title: 'Afgør', steps: [`Tryk "Reklamationer" → "Afgør", vælg udfald, og tryk "Gem". Ved "Afvist" skal der en begrundelse.`] }
      ]
    },
    {
      id: 'vehicles',
      title: 'Firmabiler',
      icon: '🚗',
      role: 'Medarbejder',
      module: 'vehicles',
      where: `"Biler" i menuen - skader også under "+" → "Meld skade på bil"`,
      intro: `Hvem kører hvilken bil, og hvornår skal den til syn, service og dækskift. Kører du selv en bil, får du besked i god tid - og du skriver km og dækskift.`,
      sketch: {
        device: 'phone',
        title: 'Biler',
        nav: true,
        blocks: [
          { t: 'row', text: 'CX 81 402 · Bil 3', right: 'Din bil', n: 1 },
          { t: 'text', text: '⚠ Syn om 10 dage · Service om 800 km' },
          { t: 'btn', text: 'Gem km', n: 2 },
          { t: 'btn', text: '❄ Vinterdæk er på', n: 3 },
          { t: 'btn', text: '🚗 Meld skade', primary: true, n: 4 }
        ]
      },
      flows: [
        {
          title: 'Hold øje med din bil',
          steps: [n(1, `Tryk på bilen. Orange betyder snart, rødt betyder overskredet. Du og kontoret får en besked 30 dage før syn, service, forsikring og leasing.`)]
        },
        {
          title: 'Skriv km og dækskift',
          steps: [
            n(2, `Skriv hvad km-tælleren viser, og tryk "Gem km". Så ved systemet, hvornår bilen skal til service.`),
            n(3, `Har du fået skiftet dæk, så tryk "Vinterdæk er på" eller "Sommerdæk er på". Har bilen helårsdæk, er knappen der ikke.`)
          ]
        },
        {
          title: 'Meld en skade',
          steps: [
            n(4, `Tryk "Meld skade", skriv hvor på bilen og hvad der er sket, og tag billeder af skaden - du kan tage flere efter hinanden.`),
            `Sæt flueben, hvis HC-Auto, autohjælp, politi eller ambulance er tilkaldt.`,
            `Var der en anden bil involveret, så sæt fluebenet "Der er en modpart", skriv nummerplade, navn, telefon og forsikringsselskab, og tag billeder af den anden bil. Det ser kun kontoret og dig.`,
            `Kontoret får besked og følger op. Du får besked, når skaden er bestilt eller udbedret.`
          ]
        }
      ],
      tips: [
        `Vinterdæk skal være på senest 15. oktober og sommerdæk senest 15. april - du får besked 3 uger før. Med helårsdæk kommer der ingen besked om dækskift.`,
        `Km kan kun skrives højere end sidst. Har du tastet forkert, så spørg kontoret.`
      ]
    },
    {
      id: 'vehicles-manage',
      title: 'Opret og ret biler',
      icon: '🚙',
      role: 'Administrator',
      module: 'vehicles',
      where: `"Biler" → "Opret bil" - eller "+" → "Ny bil"`,
      intro: `Kontoret opretter bilerne, vælger føreren og skriver datoerne. Skaderne følges op her.`,
      flows: [
        {
          title: 'Opret en bil',
          steps: [
            `Tryk "Opret bil", skriv nummerpladen, vælg gul, hvid eller gul/hvid plade, og skriv gerne et navn som "Bil 3".`,
            `Under "Billede af bilen" kan du lægge et billede på - det vises forrest i listen, så bilen kan kendes, og på bilens side. Billedet gøres mindre af sig selv, før det sendes. "Fjern billedet" tager det af igen.`,
            `Vælg føreren, og skriv næste syn, service (dato og/eller km), forsikring, leasing og hvilke dæk der sidder på - sommer-, vinter- eller helårsdæk.`,
            `Har bilen et lager, så vælg det under "Bilens lager".`,
            `Skriv nummeret på ekstranøglen i nøgleskabet under "Ekstranøgle nr. i nøgleskabet" - det står på bilens side.`
          ]
        },
        {
          title: 'Følg op på en skade',
          steps: [
            `Åbn bilen, tryk "Følg op" ved skaden, vælg Ny, Bestilt eller Udbedret, og skriv hvad der er gjort.`,
            `Den, der meldte skaden, får besked.`
          ]
        },
        {
          title: 'Bilen er solgt',
          steps: [`Tryk "Ret bilen", og fjern fluebenet ved "I brug". En bil med skader i historikken kan ikke slettes.`]
        }
      ],
      tips: [`Husk at rette datoen, når bilen har været til syn eller service - ellers varsles der om den gamle.`]
    },
    {
      id: 'equipment',
      title: 'Værktøj',
      icon: '🪜',
      role: 'Medarbejder',
      module: 'equipment',
      where: `"Værktøj" i menuen`,
      intro: `Stiger, måleudstyr og andet grej: hvem har det, hvornår det er fri igen, og hvornår det skal efterses. Hvert stykke har et QR-mærkat, du kan scanne.`,
      flows: [
        {
          title: 'Tag grej med',
          steps: [
            `Scan QR-mærkatet på værktøjet - eller find det i listen og tryk "Åbn".`,
            `Tryk "Tag den". Så står den på dig, indtil du afleverer den.`
          ]
        },
        { title: 'Aflevér grej', steps: [`Find grejet, tryk "Afleveret" (eller "Aflevér" på værktøjets side), og bekræft.`] },
        {
          title: 'Reservér til en bestemt dag',
          steps: [
            `Tryk "Åbn" ud for værktøjet, og tryk "Reservér".`,
            `Vælg fra hvilken dag og hvor mange dage du regner med, og tryk "Reservér".`
          ]
        },
        {
          title: 'Meld det defekt',
          steps: [
            `Tryk "Åbn" ud for værktøjet, og tryk "Til service" eller "Defekt" ved Tilstand.`,
            `Skriv hvad der er galt. Så kan ingen tage det med, før det er bragt i stand.`
          ]
        },
        {
          title: 'Skriv hvad du har lavet',
          steps: [
            `Tryk "Åbn" ud for værktøjet, og tryk "Skriv service" under "Service og eftersyn".`,
            `Skriv hvad du har lavet, og hvad du har skiftet - f.eks. "renset og smurt", "kul og netledning".`,
            `Tag gerne et billede med. Tryk "Gem service".`
          ]
        }
      ],
      tips: [
        `Du kan kun aflevere grej, du selv har - og kun skifte tilstand på det, du står med.`,
        `Servicelogget må alle skrive i. Selve eftersynet, der rykker næste eftersynsdato, laves af en administrator.`,
        `Billeder kan lægges på med det samme eller bagefter med "📷 Tilføj billede" på posten.`,
        `Har en anden reserveret grejet i dag, kan du ikke tage det med. Der står, hvem det er, og hvor længe.`,
        `Antal dage er et skøn. Det spærrer ikke - det gør bare, at den næste kan se, hvornår grejet formentlig er fri.`,
        `Er mærkatet slidt, kan du taste de otte tegn under QR-koden i scanneren.`
      ]
    },
    {
      id: 'equipment-manage',
      title: 'Udlevér værktøj og registrér eftersyn',
      icon: '🧰',
      role: 'Projektleder',
      module: 'equipment',
      where: `"Værktøj"`,
      intro: `Projektledere udleverer grej til andre og kan aflyse reservationer. Oprettelse, foto og eftersyn laves af en administrator.`,
      flows: [
        {
          title: 'Udlevér',
          steps: [
            `Tryk "Udlevér", vælg medarbejder, evt. hvor mange dage og hvilken sag, og tryk "Udlevér".`
          ]
        },
        { title: 'Aflys en reservation', steps: [`Tryk "Åbn" ud for værktøjet, find reservationen, og tryk "Aflys".`] },
        { title: 'Eftersyn (administrator)', steps: [`Tryk "Eftersyn", udfyld, og tryk "Gem eftersyn".`] }
      ],
      tips: [
        `Kasseret grej: fjern fluebenet "Grejet er i brug" frem for at slette - sletning fjerner historikken.`,
        `Er grejet reserveret af en anden, skal reservationen aflyses, før det kan udleveres til nogen anden.`
      ]
    },
    {
      id: 'equipment-admin',
      title: 'Opret værktøj med foto og QR-mærkat',
      icon: '🏷️',
      role: 'Administrator',
      module: 'equipment',
      where: `"Værktøj" og "Værktøjskategorier" i adminmenuen`,
      intro: `Administrator opretter værktøjet, sætter kategori og tilstand, lægger et foto på og printer QR-mærkatet.`,
      flows: [
        {
          title: 'Opret værktøj',
          steps: [
            `Tryk "Opret værktøj".`,
            `Udfyld navn, vælg kategori og tilstand, og skriv type, mærkning, fabrikat og serienummer.`,
            `Sæt et eftersynsinterval, hvis det skal efterses - en stige skal, en boremaskine ikke.`,
            `Tryk "Gem". Systemet giver selv værktøjet en kode til QR-mærkatet.`
          ]
        },
        {
          title: 'Foto',
          steps: [
            `Tryk "Åbn" ud for værktøjet, og tryk "Tag eller vælg foto". Knappen findes kun der - ikke i listen.`,
            `Tag billedet med bagkameraet, eller vælg et fra telefonen. Det skaleres af sig selv.`
          ]
        },
        {
          title: 'QR-mærkat',
          steps: [
            `Tryk "Åbn" ud for værktøjet, og tryk "QR-mærkat". QR-koden står også øverst på siden.`,
            `Vælg antal kopier og "Printer" (Brother QL 62 mm endeløs eller ark), tryk "Print", og sæt mærkatet et sted, hvor det ikke slides af.`
          ]
        },
        {
          title: 'Kategorier',
          steps: [
            `Gå til "Værktøjskategorier", skriv navnet og evt. et tegn, og tryk "Tilføj".`,
            `Brug pilene til at flytte op og ned. "Slå fra" skjuler en kategori uden at røre det, der står i den.`
          ]
        }
      ],
      tips: [
        `Én række er ét fysisk stykke grej. Tre stiger står som tre rækker - ellers kan man ikke se, hvem der har hvilken.`,
        `En kategori med værktøj i kan ikke slettes. Flyt værktøjet først, eller slå kategorien fra.`,
        `Mærkatet peger på /m/ og koden. Det virker også med mobilens eget kamera.`
      ]
    },
    {
      id: 'keys',
      title: 'Nøgler og adgangsbrikker',
      icon: '🗝️',
      role: 'Medarbejder',
      module: 'keys',
      where: `"Nøgler" i menuen`,
      intro: `Husets fysiske nøgler og brikker: hvem har dem lige nu. Koder til porte og nøglebokse hører ikke hjemme her - de står krypteret på sagen under "🔑 Port og nøgleboks".`,
      flows: [
        { title: 'Hvem har nøglen?', steps: [`Søg på kunde eller sted, og læs navnet i rækken.`] },
        { title: 'Aflevér en nøgle', steps: [`Tryk "Afleveret", og bekræft.`] }
      ]
    },
    {
      id: 'keys-manage',
      title: 'Udlevér nøgler',
      icon: '🔏',
      role: 'Projektleder',
      module: 'keys',
      where: `"Nøgler"`,
      intro: `Projektledere udleverer nøgler. Nye nøgler oprettes af en administrator.`,
      flows: [{ title: 'Udlevér', steps: [`Tryk "Udlevér", vælg medarbejder, og tryk "Udlevér".`] }],
      tips: [`En bortkommet nøgle: fjern fluebenet "Nøglen findes stadig" frem for at slette.`]
    },
    {
      id: 'certificates',
      title: 'Certifikater og kurser',
      icon: '🎓',
      role: 'Medarbejder',
      module: 'certificates',
      where: `"Certifikater" i menuen`,
      intro: `Hvem har hvilke beviser, og hvornår udløber de. Beviserne registreres af en administrator.`,
      flows: [{ title: 'Find en med et bestemt bevis', steps: [`Søg, f.eks. på "køle", og læs udløbet.`] }],
      tips: [`Et udløbet bevis betyder, at man ikke må lave det arbejde, beviset dækker.`]
    },
    {
      id: 'certificates-manage',
      title: 'Registrér beviser',
      icon: '📜',
      role: 'Administrator',
      module: 'certificates',
      where: `"Certifikater" → "Registrér bevis"`,
      intro: `Skriv beviset ind med udløbsdato, så der varsles i tide.`,
      flows: [{ title: 'Registrér', steps: [`Tryk "Registrér bevis", udfyld, og tryk "Gem".`] }],
      tips: [`Uden "Gyldigt til" varsles der aldrig. Er beviset fornyet, så fjern fluebenet "Gyldigt bevis" på det gamle.`]
    },
    {
      id: 'absences',
      title: 'Fravær og ferie',
      icon: '🌴',
      role: 'Medarbejder',
      module: 'absence',
      where: `"Fravær" i menuen`,
      intro: `Skriv din ferie og dit fravær ind, så det står i kalenderen. Du ser også, hvem der er væk i dag.`,
      flows: [
        {
          title: 'Skriv din ferie ind',
          steps: [`Tryk "Registrér fravær".`, `Vælg hvad, første og sidste dag.`, `Tryk "Gem".`]
        }
      ],
      tips: [`Der er ingen godkendelse og ingen feriesaldo her - det hører til lønsystemet. Begge dage tæller med.`]
    },
    {
      id: 'absences-manage',
      title: 'Registrér fravær for andre',
      icon: '📋',
      role: 'Projektleder',
      module: 'absence',
      where: `"Fravær"`,
      intro: `Projektledere kan registrere, rette og slette fravær for alle.`,
      flows: [{ title: 'For en kollega', steps: [`Tryk "Registrér fravær", vælg personen under "Hvem", og udfyld.`] }]
    },
    {
      id: 'manual-shelves',
      title: 'Manualhylder',
      icon: '🗂️',
      role: 'Administrator',
      module: 'manuals',
      where: `Administration → "Manualhylder"`,
      intro: `Hylderne, manualerne sorteres på.`,
      flows: [{ title: 'Ryd op', steps: [`Omdøb, flyt med ↑/↓, skjul eller fjern en hylde.`] }],
      tips: [`Fjernes en hylde, bliver manualerne stående uden hylde.`]
    },
    {
      id: 'vpn-technician',
      title: 'VPN-adgang til et kundeanlæg',
      icon: '🔐',
      role: 'Medarbejder',
      module: 'vpn',
      where: `Menuen → "VPN-adgang" (på telefonen under "Mere")`,
      intro: `Lav din VPN-nøgle én gang og læg den i WireGuard-appen. Når en Superadmin giver dig adgang til et anlæg, slår du tunnelen til - adgangen lukker af sig selv igen.`,
      sketch: {
        device: 'phone',
        title: 'VPN-adgang',
        nav: true,
        blocks: [
          { t: 'btn', text: 'Lav VPN-nøgle', primary: true, n: 1 },
          {
            t: 'card',
            title: 'Din VPN-konfiguration',
            children: [
              { t: 'banner', text: 'Vises kun nu', tone: 'warn' },
              { t: 'qr', label: 'Scan i WireGuard-appen', n: 2 },
              { t: 'btns', items: [{ text: 'Hent fil', n: 3 }, { text: 'Den er lagt ind - skjul', primary: true }] }
            ]
          },
          { t: 'card', title: 'Forbind til et anlæg', children: [{ t: 'field', label: 'Kunde', value: 'Østparken' }, { t: 'field', label: 'Anlæg', value: 'Batterianlæg 1 (2 timer)' }, { t: 'btn', text: '🔑 Forbind', primary: true, n: 4 }] },
          { t: 'card', title: 'Din adgang lige nu', children: [{ t: 'row', text: 'Batterianlæg 1', meta: 'åben til kl. 14:30', right: 'Stop adgang', n: 5 }] }
        ]
      },
      flows: [
        {
          title: 'Første gang',
          steps: [
            `Installér appen "WireGuard" - der er links til App Store, Google Play og wireguard.com (Windows, Mac og Linux) øverst på siden.`,
            n(1, `Tryk "Lav VPN-nøgle". Nøglen laves på din telefon og sendes ikke nogen steder hen.`),
            n(2, `På en computer: scan QR-koden med WireGuard-appen ("+" → "Scan QR-kode").`),
            n(3, `På telefonen: tryk "Hent fil", og importér filen i WireGuard-appen.`)
          ]
        },
        {
          title: 'Når en Superadmin har godkendt dig',
          steps: [
            n(4, `Under "Forbind til et anlæg": vælg kunden og anlægget. Listen viser anlæggets alias, og under valget står routerens adresse og hvad du skal skrive for at nå kundens udstyr. Tryk "Forbind", når du er klar - bekræft med din YubiKey, hvis den kræves. Intet åbner før, og tiden løber fra nu. Du kan kun være forbundet til ét anlæg ad gangen.`),
            `Slå tunnelen til i WireGuard-appen og gå på anlægget. Routerens egen adresse står under "Din adgang lige nu".`,
            `Udstyr på kundens LAN (batteri, inverter): skriv adressen fra kundens dokumentation i feltet under anlægget - siden viser, hvad du skal skrive i stedet (fx 10.122.1.50 = 10.52.0.50). Udstyret ser dig som routerens egen adresse på kundens net (typisk .240) - siden viser adressen under anlægget.`,
            n(5, `Tryk "Stop adgang", når du er færdig. Ellers lukker den af sig selv.`)
          ]
        }
      ],
      tips: [
        `Konfigurationen vises kun én gang. Er den væk, eller har du fået ny telefon: tryk "Lav ny nøgle".`,
        `Nøglen alene giver ingen adgang - en Superadmin skal godkende, og du skal selv åbne adgangen inden 24 timer.`,
        `Kun én montør kan være på et anlæg ad gangen. Siger siden, at anlægget er i brug, så vent til den anden har trykket "Stop adgang".`,
        `Står der, at din konfiguration er fra før LAN-adgang: tryk "Lav ny nøgle" og læg den nye i appen - ellers kan du ikke nå udstyret bag routeren.`,
        `YubiKey: sæt den i (eller hold den mod telefonen), og tryk på den, når Microsoft beder om det. Den skal være registreret i Microsoft 365 først.`
      ]
    }
  ]
}
